import { createContext, useContext, useEffect, useState } from 'react';

/**
 * Whether this viewer has asked their device for less motion.
 *
 * `prefers-reduced-motion` is an OPERATING SYSTEM setting, not anything this app stores — iOS
 * Accessibility > Motion, Android "Remove animations", macOS Display > Reduce motion, Windows
 * Visual effects. That is why there is no account preference to go with it: reading the device
 * means it works before a student has an account at all, and applies everywhere they go.
 *
 * Starts `false` and corrects on mount rather than reading during render, so server/first paint and
 * the client agree. The listener matters as much as the initial read — the setting can be changed
 * while a page is open, and on iOS it is changed from Control Centre mid-session.
 *
 * `spine-builder.tsx` still carries its own inline copy of this (it gates the card glide and the
 * jump pulse). Fold it in when that file is next open; a second copy of a media query is how the
 * two end up disagreeing about what the viewer asked for.
 */
/**
 * Preview's "simulate reduced motion" switch.
 *
 * The OS setting is the real signal and this never touches it — a teacher cannot change a
 * student's device from here, and a toggle that pretended to would be lying about where the
 * setting lives. This is the same kind of thing as the colour-vision filters beside it: a way to
 * SEE the experience someone else will get. It ORs with the real query, so a teacher who has the
 * OS setting on sees reduced motion whether the switch is on or not.
 */
export const ReducedMotionOverrideContext = createContext(false);

export function useReducedMotion(): boolean {
  const override = useContext(ReducedMotionOverrideContext);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduced(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  return reduced || override;
}
